import type { EventVaultType, EventWebsiteUrlMode, ExperienceType, IInvitationResponse, ProductKey, ZipStatus } from "@/models"
import type { ILocationRequest, ILocationResponse, ILocationResponseV1, SocialsBehavior, TaggingBehavior } from "@/types"

export enum WorkaroundType {
  All,
  Login,
  ComingSoon,
  Marketing,
  Welcome,
}

export interface IEventVaultRedirectWorkaround {
  RedirectUrl?: string
  Type?: WorkaroundType
}

export enum EventVaultPageMode {
  Invitation = 0,
  Live = 1,
}

export interface IEventVaultRequest {
  Title?: string
  EventHeader?: string
  Description?: string
  EventDate?: string
  EventEndDate?: string
  Timezone?: string
  CoverImage?: File | null
  CoverImageUrl?: string
  WelcomeVideo?: File | null
  WelcomeVideoUrl?: string
  EventVaultTypeId?: EventVaultType
  Location?: ILocationRequest
  CommunicationLocale?: string
}

export interface IEventVaultResponseV1 {
  eventVaultId: number
  isHost?: boolean
  isGuest?: boolean
  pageMode?: EventVaultPageMode
  title?: string
  eventVaultTypeId?: number
  eventHeader?: string
  description?: string
  coverImageURL?: string
  featureImageUrl?: string
  pathToWelcomeVideo?: string
  accessToken?: string
  eventVaultStatus: number
  eventDate?: string | Date
  eventEndDate?: string | Date
  timezone?: string
  eventCloseDate?: string | Date
  eventExpiryDate?: string | Date
  accessedDate?: string | Date
  eventGuestCount?: number
  totalPics?: number
  totalVids?: number
  totalMessages?: number
  totalAlbums?: number
  qrCodeUrl?: string
  eventShareLink?: string
  qrLink?: string
  gpxProductId?: number
  gpxProductKey?: ProductKey
  gpxProductName?: string
  passcode?: string
  location?: ILocationResponseV1
  invitation?: IInvitationResponse
  upgradeUrl?: string
  freemiumMode?: boolean
  MaxPhotos?: number
  PhotoCount?: number
  communicationLocale?: string
}

export interface IEventVaultResponse {
  EventVaultID: number
  PackageSubscriptionID?: number
  IsHost?: boolean
  IsGuest?: boolean
  PageMode?: EventVaultPageMode
  Workaround?: IEventVaultRedirectWorkaround
  Title?: string
  EventVaultTypeID?: number
  EventHeader?: string
  Description?: string
  CoverImageURL?: string
  PathToFeatureImage?: string
  PathToWelcomeVideo?: string
  AccessToken?: string
  EventVaultSettings?: IEventVaultSettingsResponse
  EventTemplate?: IEventTemplateResponse
  EventVaultStatus: number
  EventDate?: string | Date
  EventEndDate?: string | Date
  Timezone?: string
  EventCloseDate?: string | Date
  EventExpiryDate?: string | Date
  AccessedDate?: string | Date
  ZipJobSummary?: IEventVaultZipSummaryResponse
  EventGuestCount?: number
  TotalPics?: number
  TotalVids?: number
  TotalMessages?: number
  TotalAlbums?: number
  FullPathToQRCode?: string
  EventShareLink?: string
  QRLink?: string
  GPXProductID?: number
  GPXProductKey?: ProductKey
  GPXProductName?: string
  Passcode?: string
  Location?: ILocationResponse
  UseDirectUpload?: boolean
  FreemiumMode?: boolean
  UpgradeUrl?: string
  MaxPhotos?: number
  PhotoCount?: number
  CommunicationLocale?: string
  EventWebsite?: {
    DomainKey?: string
    Slug?: string
    IsPublic?: boolean
    DateCreated?: Date
    LastUpdated?: Date
  }
}

export interface IEventVaultZipSummaryResponse {
  Id?: number
  FileName?: string
  Status?: ZipStatus
  DownloadURL?: string
  ExpiryDate?: string
  CompletedDate?: string
}

export interface IEventVaultSettingsResponse {
  EventVaultSettingsID: number
  EventVaultID: number
  SharePixWithGuests: boolean
  EventGuestMax: number
  VideoEnabled: boolean
  AlbumsEnabled: boolean
  AllowVideoGuestbook: boolean
  AllowAlbumNavigation: boolean
  AllowRootDirectoryUpload: boolean
  AllowGuestDownload: boolean
  AllowHostDownload: boolean
  AllowEventTemplate: boolean
  StreamingAllowed: boolean
  ShareGuestbookWithGuests: boolean
  GuestEmailRequired: boolean
  HideGuestEmail: boolean
  HideGuestbook: boolean
  HideConfetti: boolean
  AllowGuestUpload: boolean
  MaxNoOfAlbums: number
  DisableGuestUpload: boolean
  HideMainGallery: boolean
  SocialsBehavior: SocialsBehavior
  TaggingBehavior: TaggingBehavior
  AllowGuestZipDownload: boolean
  InvitationEnabled?: boolean
  AllowThemeEdit?: boolean
  ModeratePhotosAndVideos?: boolean
  ModerateMessagesAndComments?: boolean
  ExperienceType?: ExperienceType
  RSVPEmailEnabled?: boolean
  EventWebsiteUrlMode?: number
  WishlistMode?: number
}

export interface ICustomSocialLinksResponse {
  Facebook: string
  Instagram: string
  Youtube: string
  Tiktok: string
  Pinterest: string
  X: string
  LinkedIn: string
}

export interface ICustomSocialLinksResponseV1 {
  facebook: string
  instagram: string
  youtube: string
  tiktok: string
  pinterest: string
  x: string
  linkedIn: string
}

export interface ICustomLinkResponse {
  Key?: string
  Value?: string | null
}

export interface ICustomLinkResponseV1 {
  key?: string
  value?: string | null
}

export interface ICustomFooterLinksResponse {
  SocialLinks: ICustomSocialLinksResponse | null
  FooterLinks: Array<ICustomLinkResponse> | null
}

export interface ICustomFooterLinksResponseV1 {
  socialLinks: ICustomSocialLinksResponseV1 | null
  footerLinks: Array<ICustomLinkResponseV1> | null
}

export interface IEventThemeJsonTokens {
  "--template-header-background"?: string
  "--template-footer-background"?: string
  "--template-primary-colour"?: string
  "--template-secondary-colour"?: string
  "--template-link-text"?: string
  "--template-nav-text"?: string
  "--template-primary-button-text"?: string
  "--template-secondary-button-text"?: string
  "--template-primary-card-text"?: string
  "--template-secondary-card-text"?: string
  "--template-border"?: string
  "--template-heading-font"?: string
  "--template-base-font"?: string
}

export interface IEventThemeJson {
  tokens?: IEventThemeJsonTokens
  typography?: {
    headingFontValue?: string
    baseFontValue?: string
  }
}

export interface IEventThemeJsonResponse {
  Tokens?: IEventThemeJsonTokens
  Typography?: {
    HeadingFontValue?: string
    BaseFontValue?: string
  }
}

export interface IEventThemeJsonResponseV1 {
  tokens?: IEventThemeJsonTokens
  typography?: {
    headingFontValue?: string
    baseFontValue?: string
  }
}

export interface IEventThemeJsonRequestV1 {
  tokens?: IEventThemeJsonTokens
  typography?: {
    headingFontValue?: string
    baseFontValue?: string
  }
}

export interface IEventTemplateResponse {
  EventTemplateID?: number
  Id?: number
  DateCreated: Date
  LastUpdated: Date
  Name?: string
  Description?: string
  ThemeJSON?: IEventThemeJsonResponse
  TemplateCSS?: string
  BaseFontLabel?: string
  BaseFontValue?: string
  HeadingFontLabel?: string
  HeadingFontValue?: string
  CollectGuestEmailsForMarketing?: boolean
  RequireAdditionalHostTerms?: boolean
  AdditionalHostTermsHTML?: string
  OptInSentenceHTML?: string
  AdditionalHostTerm?: ICustomLinkResponse
  OptInSentence?: ICustomLinkResponse
  PathToFooterImage?: string
  PathToHeaderImage?: string
  TemplateSettings?: {
    CustomFooterLinks: ICustomFooterLinksResponse
  }
  IsApplied?: boolean
}

export interface IEventTemplateResponseV1 {
  eventTemplateID?: number
  id?: number
  dateCreated?: Date
  lastUpdated?: Date
  name?: string
  description?: string
  themeJSON?: IEventThemeJsonResponseV1
  templateCSS?: string
  baseFontLabel?: string
  baseFontValue?: string
  headingFontLabel?: string
  headingFontValue?: string
  collectGuestEmailsForMarketing?: boolean
  requireAdditionalHostTerms?: boolean
  additionalHostTermsHtml?: string
  optInSentenceHtml?: string
  additionalHostTerm?: ICustomLinkResponseV1
  optInSentence?: ICustomLinkResponseV1
  pathToFooterImage?: string
  pathToHeaderImage?: string
  templateSettings?: {
    customFooterLinks: ICustomFooterLinksResponseV1
  }
  isApplied?: boolean
}

export interface IEventVaultSettingsUpdate {
  shareGalleryWithGuests: boolean
  allowGuestDownload: boolean
  allowGuestUpload: boolean
  allowStreamingVids: boolean
  hideMainGallery: boolean
  enableGuestbook: boolean
  allowGuestbookVideo: boolean
  shareGuestbookWithGuests: boolean
  hideGuestEmail: boolean
  allowGuestZipDownload: boolean
  enableReactions?: boolean
  enableComments?: boolean
  enableTags?: boolean
  enableInvitation?: boolean
  moderatePhotosAndVideos?: boolean
  moderateMessagesAndComments?: boolean
  experienceType?: ExperienceType
  enableEventWebsite?: boolean
}

export interface IEventVaultSettingsUpdateRequest {
  sharePixWithGuests: boolean
  allowGuestDownload: boolean
  disableGuestUpload: boolean
  streamingAllowed: boolean
  hideMainGallery: boolean
  hideGuestbook: boolean
  allowVideoGuestbook: boolean
  shareGuestbookWithGuests: boolean
  hideGuestEmail: boolean
  allowGuestZipDownload: boolean
  socialsBehavior?: SocialsBehavior
  taggingBehavior?: TaggingBehavior
  invitationEnabled?: boolean
  moderatePhotosAndVideos?: boolean
  moderateMessagesAndComments?: boolean
  experienceType?: ExperienceType
  eventWebsiteUrlMode?: EventWebsiteUrlMode
}

export interface IEventExtendPricing {
  gpxProductID: number
  originalPrice: number
  discountedPrice: number
  discountPercentage: number
  isoCurrencyCode: string
  currencySymbol: string
  extendEventURL: string
}

export interface IEventExtendPricingResponse {
  GPXProductID: number
  RegularPrice: number
  SalePrice: number
  Percentage: number
  ISOCurrencyCode: string
  CurrencySymbol: string
  ExtendURL: string
}
